import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Panel, Stat, State } from "./Page";

const DOT = { ok: "bg-buy", warn: "bg-primary", down: "bg-sell" };
const TXT = { ok: "text-buy", warn: "text-primary", down: "text-sell" };
const WORD = { ok: "RUNNING", warn: "DEGRADED", down: "DOWN" };
const LABEL_WORD = { deriv: { ok: "CONNECTED" }, relay: { ok: "LIVE" }, catalog: { ok: "SYNCED" }, history: { ok: "READY" }, trading: { ok: "READY" }, replay: { ok: "READY" }, db: { ok: "HEALTHY" } };

// Measures the real frame rate of the page (chart renders through requestAnimationFrame).
function useFps() {
  const [fps, setFps] = useState(null);
  useEffect(() => {
    let frames = 0, last = performance.now(), raf;
    const tick = (now) => { frames++; if (now - last >= 1000) { setFps(Math.round((frames * 1000) / (now - last))); frames = 0; last = now; } raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return fps;
}

// R.I.O System Health — REAL telemetry from GET /api/system/health (10 s poll) + browser FPS.
const ms = (s) => (s ? `${Math.round(s.last)} ms` : "—");
const sub = (s) => (s ? `avg ${Math.round(s.avg)} · ${s.min}–${s.max} · n=${s.n}` : "no samples yet");
const GRADE = { excellent: "text-buy", good: "text-primary", degraded: "text-sell" };

// Measured latencies from GET /api/trading/telemetry (Deriv ping, proposal/BUY RTT, tick arrival, clock offset) + browser ↔ Fela RTT.
function Telemetry() {
  const [t, setT] = useState(null);
  const [rtt, setRtt] = useState(null);
  useEffect(() => {
    const load = async () => {
      const t0 = performance.now();
      try { const { data } = await api.get("/trading/telemetry"); setRtt(Math.round(performance.now() - t0)); setT(data); } catch {}
    };
    load(); const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);
  if (!t) return <div className="text-xs text-muted-foreground">Loading telemetry…</div>;
  const tw = t.trading_ws || {}, mw = t.market_ws || {};
  return (
    <div className="space-y-3 text-xs" data-testid="telemetry">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
        <Stat testid="tel-market-ws" label="Market WebSocket" value={<span className={mw.connected ? "text-buy" : "text-sell"}>{mw.connected ? "Connected" : "Down"}</span>} sub={`${mw.streamed_symbols ?? 0} symbols · auth ${mw.auth_stream || "—"}`} />
        <Stat testid="tel-trading-ws" label="Trading WebSocket" value={<span className={tw.connected ? "text-buy" : "text-sell"}>{tw.connected ? "Connected" : tw.connection || "Down"}</span>} sub={`${tw.endpoint || ""} · ${tw.connected_for_s != null ? `${Math.floor(tw.connected_for_s / 60)}m up` : "—"} · ${t.reconnects} reconnects`} />
        <Stat testid="tel-tick-latency" label="Tick latency" value={t.tick_latency_ms ? `≈${t.tick_latency_ms.last} ms` : "—"} sub={t.tick_latency_ms ? `avg ${t.tick_latency_ms.avg} · 1s epoch resolution` : "no ticks yet"} />
        <Stat testid="tel-deriv-ping" label="Server ↔ Deriv" value={ms(t.deriv_ping_ms)} sub={sub(t.deriv_ping_ms)} />
        <Stat testid="tel-proposal-rtt" label="Proposal RTT" value={ms(t.proposal_rtt_ms)} sub={sub(t.proposal_rtt_ms)} />
        <Stat testid="tel-buy-rtt" label="BUY RTT" value={ms(t.buy_rtt_ms)} sub={sub(t.buy_rtt_ms)} tone="primary" />
        <Stat testid="tel-browser-rtt" label="Browser ↔ Fela" value={rtt != null ? `${rtt} ms` : "—"} sub="this page → API" />
        <Stat testid="tel-clock" label="Deriv clock offset" value={t.clock_offset_ms != null ? `${t.clock_offset_ms > 0 ? "+" : ""}${Math.round(t.clock_offset_ms)} ms` : "—"} sub={t.clock_synced_ago_s != null ? `synced ${Math.round(t.clock_synced_ago_s)}s ago` : "not synced"} />
      </div>
      <div className="flex items-center gap-2 font-mono-nums" data-testid="tel-health">
        <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Execution health</span>
        <span className={`font-bold ${GRADE[t.execution_health] || "text-muted-foreground"}`}>{t.execution_health ? t.execution_health.toUpperCase() : "NO PROPOSALS YET"}</span>
        {t.execute_total_ms && <span className="text-muted-foreground">· last BUY end-to-end {Math.round(t.execute_total_ms.last)} ms (server, incl. DB writes)</span>}
        {t.contract_update_ms && <span className="text-muted-foreground">· contract update {Math.round(t.contract_update_ms.avg)} ms</span>}
      </div>
    </div>
  );
}

export default function SystemHealth({ compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const fps = useFps();
  const timer = useRef(null);
  useEffect(() => {
    const load = () => api.get("/system/health").then(({ data }) => { setData(data); setError(null); }).catch((e) => setError(e.response?.data?.detail || e.message));
    load(); timer.current = setInterval(load, 10000);
    return () => clearInterval(timer.current);
  }, []);
  const m = data?.metrics;
  return (
    <div className="space-y-3" data-testid="system-health">
      <State loading={!data && !error} error={error} testid="health">
        {data && (<>
          <div className={`grid gap-2 ${compact ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4 xl:grid-cols-7"}`}>
            <Stat testid="health-tps" label="Ticks / sec" value={m.ticks_per_sec.toLocaleString()} sub={`${m.streamed_symbols} streamed`} tone="primary" />
            <Stat testid="health-symbols" label="Active symbols" value={m.active_symbols} sub="Deriv catalogue" />
            <Stat testid="health-open" label="Open demo contracts" value={m.open_contracts} tone={m.open_contracts ? "buy" : undefined} />
            <Stat testid="health-alerts" label="Active alerts" value={m.active_alerts} sub={`${m.alert_evaluations} evaluations`} />
            <Stat testid="health-scanner" label="Scanner last run" value={m.scanner_last_run_ago_s != null ? `${Math.round(m.scanner_last_run_ago_s)}s ago` : "—"} sub={`${m.scanner_results} results`} />
            <Stat testid="health-fps" label="Chart FPS" value={fps ?? "…"} tone={fps != null && fps < 30 ? "sell" : undefined} sub="browser render loop" />
            <Stat testid="health-uptime" label="Backend uptime" value={`${Math.floor(data.uptime_s / 3600)}h ${Math.floor((data.uptime_s % 3600) / 60)}m`} sub={`engine v${m.engine_version}`} />
          </div>
          <Panel title="DERIV CONNECTION · MEASURED LATENCY" testid="health-telemetry">
            <Telemetry />
          </Panel>
          <Panel title="R.I.O SYSTEM HEALTH" testid="health-components">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 font-mono-nums text-xs">
              {data.components.map((c) => (
                <div key={c.id} data-testid={`health-${c.id}`} className="flex items-center gap-3 py-1.5 border-b border-border/60">
                  <span className="w-40 text-foreground">{c.label}</span>
                  <span className={`h-2 w-2 rounded-full ${DOT[c.status]} ${c.status === "ok" ? "animate-pulse" : ""}`} />
                  <span className={`font-bold ${TXT[c.status]}`}>{c.status === "ok" ? (LABEL_WORD[c.id]?.ok ?? WORD.ok) : WORD[c.status]}</span>
                  <span className="ml-auto text-muted-foreground truncate max-w-[55%]" title={c.detail}>{c.detail}</span>
                </div>
              ))}
            </div>
          </Panel>
        </>)}
      </State>
    </div>
  );
}
